import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { CheckCircle, XCircle, Clock, MapPin, Wifi, Bluetooth, Info, Loader2 } from 'lucide-react';
import { getPendingVerifications, confirmVerification, rejectVerification } from '../api/verification';
import { verifyAttributesLocally } from '../services/attributeMatcher';

function Verifications({ user }) {
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [verifying, setVerifying] = useState({});
  const [verificationResults, setVerificationResults] = useState({});

  const loadPendingVerifications = useCallback(async () => {
    try {
      const data = await getPendingVerifications();
      setPending(data.verifications || []);
      
      // Auto-verify each one to show status
      for (const v of (data.verifications || [])) {
        checkVerificationStatus(v);
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPendingVerifications();
  }, [loadPendingVerifications]);

  const checkVerificationStatus = async (verification) => {
    try {
      const result = await verifyAttributesLocally(verification.attributes);
      setVerificationResults(prev => ({
        ...prev,
        [verification.share_id]: result
      }));
    } catch (error) {
    }
  };

  const handleVerify = async (verification) => {
    setVerifying(prev => ({ ...prev, [verification.share_id]: true }));
    
    try {
      // Get verification result
      let result = verificationResults[verification.share_id];
      if (!result) {
        result = await verifyAttributesLocally(verification.attributes);
      }
      
      if (!result.overall_match) {
        toast.error('Proximity verification failed - you may not have been at this location');
        return;
      }
      
      // Confirm with server
      const response = await confirmVerification(verification.share_id, result);
      
      if (response.photo_shared) {
        toast.success('Photo verified and added to your feed!');
        setPending(prev => prev.filter(v => v.share_id !== verification.share_id));
      }
    } catch (error) {
      toast.error(error.message || 'Verification failed');
    } finally {
      setVerifying(prev => ({ ...prev, [verification.share_id]: false }));
    }
  };

  const handleReject = async (verification) => {
    try {
      await rejectVerification(verification.share_id);
      toast.success('Verification rejected');
      setPending(prev => prev.filter(v => v.share_id !== verification.share_id));
    } catch (error) {
      toast.error(error.message);
    }
  };

  const formatDate = (timestamp) => {
    return new Date(timestamp).toLocaleString();
  };

  const renderVerificationStatus = (verification) => {
    const result = verificationResults[verification.share_id];
    if (!result) return null;
    
    const details = result.verification_details;
    
    return (
      <div style={{ 
        marginBottom: '1rem', 
        padding: '0.75rem', 
        background: result.overall_match ? 'hsl(var(--success) / 0.1)' : 'hsl(var(--muted))', 
        borderRadius: 'calc(var(--radius) - 4px)',
        border: result.overall_match ? '1px solid hsl(var(--success) / 0.3)' : 'none'
      }}>
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: '0.5rem', 
          marginBottom: '0.75rem',
          fontSize: '0.875rem',
          fontWeight: 600,
          color: result.overall_match ? 'hsl(var(--success))' : 'hsl(var(--muted-foreground))'
        }}>
          {result.overall_match ? <CheckCircle size={16} /> : <Info size={16} />}
          {result.overall_match ? 'Ready to verify!' : 'Checking proximity...'}
        </div>
        
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', fontSize: '0.8rem' }}>
          {/* GPS Status */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <MapPin size={14} style={{ color: details.gps.matched ? 'hsl(var(--success))' : 'hsl(var(--muted-foreground))' }} />
            <span>GPS: </span>
            {details.gps.matched ? (
              <span style={{ color: 'hsl(var(--success))' }}>
                ✓ Match ({details.gps.closest_distance_m}m away)
              </span>
            ) : (
              <span style={{ color: 'hsl(var(--muted-foreground))' }}>
                No match in history
              </span>
            )}
          </div>
          
          {/* WiFi Status */}
          {details.wifi.data_available && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <Wifi size={14} style={{ color: details.wifi.matched ? 'hsl(var(--success))' : 'hsl(var(--muted-foreground))' }} />
              <span>WiFi: </span>
              {details.wifi.matched ? (
                <span style={{ color: 'hsl(var(--success))' }}>
                  ✓ Same network detected
                </span>
              ) : (
                <span style={{ color: 'hsl(var(--muted-foreground))' }}>
                  Different network
                </span>
              )}
            </div>
          )}
          
          {/* Bluetooth Status */}
          {details.bluetooth.data_available && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <Bluetooth size={14} style={{ color: details.bluetooth.matched ? 'hsl(var(--success))' : 'hsl(var(--muted-foreground))' }} />
              <span>Bluetooth: </span>
              {details.bluetooth.matched ? (
                <span style={{ color: 'hsl(var(--success))' }}>
                  ✓ {details.bluetooth.matched_devices.length} device(s) match
                </span>
              ) : (
                <span style={{ color: 'hsl(var(--muted-foreground))' }}>
                  No matching devices
                </span>
              )}
            </div>
          )}
          
          {/* Overall Score */}
          <div style={{ 
            marginTop: '0.25rem', 
            paddingTop: '0.5rem', 
            borderTop: '1px solid hsl(var(--border))',
            fontWeight: 500
          }}>
            Confidence: {details.overall_score}% (need {details.threshold}%)
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="page" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh' }}>
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="page" style={{ padding: '1rem', paddingBottom: '100px' }}>
      <div className="container">
        <div style={{ marginBottom: '1.5rem' }}>
          <h1 style={{ fontSize: '1.75rem', marginBottom: '0.5rem' }}>
            Pending Verifications
          </h1>
          <p style={{ color: 'hsl(var(--muted-foreground))', fontSize: '0.875rem' }}>
            Photos that might include you - verify your proximity
          </p>
        </div>

        {pending.length === 0 ? (
          <div className="card" style={{ textAlign: 'center', padding: '3rem 1.5rem' }}>
            <CheckCircle size={48} style={{ color: 'hsl(var(--success))', margin: '0 auto 1rem' }} />
            <p style={{ color: 'hsl(var(--muted-foreground))', fontWeight: 500 }}>
              No pending verifications!
            </p>
            <p style={{ color: 'hsl(var(--muted-foreground))', fontSize: '0.875rem', marginTop: '0.25rem' }}>
              All caught up. Photos will appear here when someone photographs you.
            </p>
          </div>
        ) : (
          <div>
            {pending.map((verification) => (
              <div key={verification.share_id} className="card" style={{ marginBottom: '1rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '1rem' }}>
                  <div>
                    <h3 style={{ fontSize: '1.125rem', fontWeight: 600, marginBottom: '0.25rem' }}>
                      {verification.owner_name}
                    </h3>
                    <p style={{ fontSize: '0.875rem', color: 'hsl(var(--muted-foreground))' }}>
                      took a photo that might include you
                    </p>
                  </div>
                  <span className="badge badge-warning" style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                    <Clock size={12} />
                    Pending
                  </span>
                </div>

                {/* Photo Info */}
                <div style={{ marginBottom: '1rem', padding: '0.75rem', background: 'hsl(var(--muted))', borderRadius: 'calc(var(--radius) - 4px)', fontSize: '0.8rem' }}>
                  <div style={{ marginBottom: '0.5rem' }}>
                    <strong>📍 Location:</strong>{' '}
                    {verification.attributes.latitude?.toFixed(4)}, {verification.attributes.longitude?.toFixed(4)}
                  </div>
                  <div style={{ marginBottom: '0.5rem' }}>
                    <strong>🕐 Time:</strong> {formatDate(verification.attributes.timestamp)}
                  </div>
                  <div>
                    <strong>👤 Face Match:</strong> {(verification.similarity || 0).toFixed(1)}%
                  </div>
                </div>

                {/* Verification Status */}
                {renderVerificationStatus(verification)}

                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button
                    className="btn btn-primary"
                    onClick={() => handleVerify(verification)}
                    disabled={verifying[verification.share_id]}
                    style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}
                    data-testid="verify-button"
                  >
                    {verifying[verification.share_id] ? (
                      <Loader2 size={18} className="animate-spin" />
                    ) : (
                      <CheckCircle size={18} />
                    )}
                    {verifying[verification.share_id] ? 'Verifying...' : 'Verify & Add'}
                  </button>
                  <button
                    className="btn btn-secondary"
                    onClick={() => handleReject(verification)}
                    style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}
                    data-testid="reject-button"
                  >
                    <XCircle size={18} />
                    Not Me
                  </button>
                </div>

                <p style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))', marginTop: '0.75rem', marginBottom: 0 }}>
                  💡 Your location history is checked locally and never sent to the server.
                </p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default Verifications;
